import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import axios from 'axios'
import toast from 'react-hot-toast'

interface User {
  id: number
  email: string
  fullName: string
  role: string
  avatarUrl?: string
  createdAt: string
}

interface AuthState {
  user: User | null
  token: string | null
  isLoading: boolean
  isAuthenticated: boolean
  login: (email: string, password: string) => Promise<boolean>
  register: (email: string, password: string, fullName: string) => Promise<boolean>
  logout: () => void
  initializeAuth: () => void
  updateProfile: (data: { fullName: string; avatarUrl?: string }) => Promise<boolean>
}

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8787/api'

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      token: null,
      isLoading: false,
      isAuthenticated: false,

      login: async (email: string, password: string) => {
        set({ isLoading: true })
        try {
          const response = await axios.post(`${API_BASE_URL}/auth/login`, {
            email,
            password
          })

          const { token, user } = response.data
          
          set({
            user,
            token,
            isAuthenticated: true,
            isLoading: false
          })

          // Set default authorization header
          axios.defaults.headers.common['Authorization'] = `Bearer ${token}`
          
          toast.success('Login berhasil!')
          return true
        } catch (error: any) {
          const message = error.response?.data?.error || 'Login gagal'
          toast.error(message)
          set({ isLoading: false })
          return false
        }
      },

      register: async (email: string, password: string, fullName: string) => {
        set({ isLoading: true })
        try {
          const response = await axios.post(`${API_BASE_URL}/auth/register`, {
            email,
            password,
            fullName
          })

          const { token, user } = response.data
          
          set({
            user,
            token,
            isAuthenticated: true,
            isLoading: false
          })

          // Set default authorization header
          axios.defaults.headers.common['Authorization'] = `Bearer ${token}`
          
          toast.success('Registrasi berhasil!')
          return true
        } catch (error: any) {
          const message = error.response?.data?.error || 'Registrasi gagal'
          toast.error(message)
          set({ isLoading: false })
          return false
        }
      },

      logout: () => {
        set({
          user: null,
          token: null,
          isAuthenticated: false
        })
        
        // Remove authorization header
        delete axios.defaults.headers.common['Authorization']
        
        toast.success('Logout berhasil!')
      },

      initializeAuth: () => {
        const { token } = get()
        if (token) {
          axios.defaults.headers.common['Authorization'] = `Bearer ${token}`
          set({ isAuthenticated: true })
          
          // Verify token by getting user info
          axios.get(`${API_BASE_URL}/auth/me`)
            .then(response => {
              set({ user: response.data.user })
            })
            .catch(() => {
              // Token invalid, logout
              get().logout()
            })
        }
      },

      updateProfile: async (data: { fullName: string; avatarUrl?: string }) => {
        set({ isLoading: true })
        try {
          await axios.put(`${API_BASE_URL}/auth/profile`, data)
          
          // Update user in store
          const currentUser = get().user
          if (currentUser) {
            set({
              user: {
                ...currentUser,
                fullName: data.fullName,
                avatarUrl: data.avatarUrl
              },
              isLoading: false
            })
          }
          
          toast.success('Profile berhasil diupdate!')
          return true
        } catch (error: any) {
          const message = error.response?.data?.error || 'Update profile gagal'
          toast.error(message)
          set({ isLoading: false })
          return false
        }
      }
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({
        user: state.user,
        token: state.token,
        isAuthenticated: state.isAuthenticated
      })
    }
  )
)
